<script lang="ts">
  import { getDialog, type Dialog } from '$lib/dialogs';
  import { session } from '$lib/stores';
  import { RectangleGroup } from '@steeze-ui/heroicons';
  import { Icon } from '@steeze-ui/svelte-icon';
  import SocketBadge from '$lib/components/SocketBadge.svelte';
  import type { users_role } from '@prisma/client';
  import { invalidateAll } from '$app/navigation';
  import { onMount } from 'svelte';
  import { writable } from 'svelte/store';

  const dialog = getDialog();
  const SESSION_CHANNEL = 'session-sync';
  const sess = writable<'auth' | 'unauth' | null>(null);

  onMount(() => {
    const bc = new BroadcastChannel(SESSION_CHANNEL);

    // Listen for session changes from other tabs
    bc.onmessage = (event) => {
      if (event.data !== $sess) {
        $sess = event.data;
        invalidateAll();
      }
    };

    // Cleanup on component destroy
    return () => {
      bc.close();
    };
  });

  // Subscribe to session changes
  session.subscribe((_) => {
    const bc = new BroadcastChannel(SESSION_CHANNEL);
    bc.postMessage($session ? 'auth' : 'unauth');
    bc.close();
  });

  const openDialog = (dialog: Dialog) => () => {
    invalidateAll();
    $dialog = dialog;
  };
  const ADMIN_SCOPE: (users_role | null)[] = ['admin', 'moderator'];
  $: {
    if ($session) {
      $dialog = null;
    }
  }
</script>

{#if $session}
  <div class="flow-root justify-self-start">
    {#if ADMIN_SCOPE.includes($session.role ?? 'user')}
      <a href="/" class="group-m-2 p-2 inline-flex items-center text-gray-500 hover:bg-gray-100">
        <span class="text-xs mr-2">Inicio</span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke-width={1.5}
          stroke="currentColor"
          class="flex-shrink-0 h-4 w-4"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25"
          />
        </svg>
        <span class="sr-only">Inicio</span>
      </a>
      {#if $session.role == 'admin'}
        <a
          href="/dashboard"
          class="group-m-2 p-2 inline-flex items-center text-gray-500 hover:bg-gray-100"
        >
          <span class="text-xs mr-2">Dashboard</span>
          <svg
            class="flex-shrink-0 h-4 w-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M17 14v6m-3-3h6M6 10h2a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v2a2 2 0 002 2zm10 0h2a2 2 0 002-2V6a2 2 0 00-2-2h-2a2 2 0 00-2 2v2a2 2 0 002 2zM6 20h2a2 2 0 002-2v-2a2 2 0 00-2-2H6a2 2 0 00-2 2v2a2 2 0 002 2z"
            />
          </svg>
          <span class="sr-only">dashboard</span>
        </a>
      {/if}
      <a
        href="/workflows"
        class="hidden group-m-2 p-2 sm:inline-flex items-center text-gray-500 hover:bg-gray-100"
      >
        <span class="text-xs mr-2">Workflows</span>
        <Icon src={RectangleGroup} class="flex-shrink-0 h-4 w-4" />
        <span class="sr-only">workflows</span>
      </a>
      {#if $session.role == 'admin'}
        <a
          href="/reportes"
          class="hidden group-m-2 p-2 sm:inline-flex items-center text-gray-500 hover:bg-gray-100"
        >
          <span class="text-xs mr-2">Reportes</span>
          <svg
            class="flex-shrink-0 h-4 w-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z"
            />
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z"
            />
          </svg>
          <span class="sr-only">reportes</span>
        </a>
      {/if}
    {/if}
    <a
      href="/mis-lotes"
      class="hidden group-m-2 p-2 sm:inline-flex items-center text-gray-500 hover:bg-gray-100"
    >
      <span class="text-xs mr-2">Mis lotes</span>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke-width="1.5"
        stroke="currentColor"
        class="flex-shrink-0 h-4 w-4"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          d="M15.75 10.5V6a3.75 3.75 0 10-7.5 0v4.5m11.356-1.993l1.263 12c.07.665-.45 1.243-1.119 1.243H4.25a1.125 1.125 0 01-1.12-1.243l1.264-12A1.125 1.125 0 015.513 7.5h12.974c.576 0 1.059.435 1.119 1.007zM8.625 10.5a.375.375 0 11-.75 0 .375.375 0 01.75 0zm7.5 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z"
        />
      </svg>

      <span class="sr-only">mis lotes</span>
    </a>
  </div>
  <div class="ml-4 lg:ml-8 flex justify-self-end items-center">
    <a href="/perfil" class="group-m-2 p-2 flex items-center text-gray-500 hover:bg-gray-100">
      <span class="text-xs mr-2">Hola {$session.username}</span>
      <svg
        class="flex-shrink-0 h-4 w-4"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="currentColor"
      >
        <path
          fill-rule="evenodd"
          d="M7.5 6a4.5 4.5 0 119 0 4.5 4.5 0 01-9 0zM3.751 20.105a8.25 8.25 0 0116.498 0 .75.75 0 01-.437.695A18.683 18.683 0 0112 22.5c-2.786 0-5.433-.608-7.812-1.7a.75.75 0 01-.437-.695z"
          clip-rule="evenodd"
        />
      </svg>
      <span class="sr-only">mi cuenta</span>
    </a>
    <SocketBadge />
  </div>
{:else}
  <div class="ml-4 flow-root lg:ml-8 flex- justify-self-start" />
  <div class="flex justify-between items-center justify-self-end">
    <a
      href="/registrarse"
      class="flex text-xs text-gray-500 p-2 hover:text-gray-900 items-center justify-center hover:bg-gray-100"
      on:click|preventDefault={openDialog('signup')}
    >
      Registrarse
      <svg
        class="flex-shrink-0 h-4 w-4"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="currentColor"
      >
        <path
          d="M6.25 6.375a4.125 4.125 0 118.25 0 4.125 4.125 0 01-8.25 0zM3.25 19.125a7.125 7.125 0 0114.25 0v.003l-.001.119a.75.75 0 01-.363.63 13.067 13.067 0 01-6.761 1.873c-2.472 0-4.786-.684-6.76-1.873a.75.75 0 01-.364-.63l-.001-.122zM19.75 7.5a.75.75 0 00-1.5 0v2.25H16a.75.75 0 000 1.5h2.25v2.25a.75.75 0 001.5 0v-2.25H22a.75.75 0 000-1.5h-2.25V7.5z"
        />
      </svg>
    </a>
    <a
      href="/ingresar"
      class="ml-8 flex items-center p-2 justify-center text-xs text-gray-500 hover:text-gray-900 hover:bg-gray-100 cursor-pointer"
      on:click|preventDefault={openDialog('signin')}
    >
      Ingresar
      <svg
        class="flex-shrink-0 h-4 w-4"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="currentColor"
      >
        <path
          fill-rule="evenodd"
          d="M7.5 3.75A1.5 1.5 0 006 5.25v13.5a1.5 1.5 0 001.5 1.5h6a1.5 1.5 0 001.5-1.5V15a.75.75 0 011.5 0v3.75a3 3 0 01-3 3h-6a3 3 0 01-3-3V5.25a3 3 0 013-3h6a3 3 0 013 3V9A.75.75 0 0115 9V5.25a1.5 1.5 0 00-1.5-1.5h-6zm5.03 4.72a.75.75 0 010 1.06l-1.72 1.72h10.94a.75.75 0 010 1.5H10.81l1.72 1.72a.75.75 0 11-1.06 1.06l-3-3a.75.75 0 010-1.06l3-3a.75.75 0 011.06 0z"
          clip-rule="evenodd"
        />
      </svg>
    </a>
    <SocketBadge />
  </div>
{/if}
